import React, { useState, useEffect } from 'react';
import LandingPage from './components/LandingPage';
import LearnTab from './components/LearnTab';
import HomeFeed from './components/HomeFeed';
import AuthPage from './components/AuthPage';
import Dashboard from './components/Dashboard';
import LegalPage from './components/LegalPage';
import AdminDashboard from './components/AdminDashboard';
import api from './api';

function App() {
  const [view, setView] = useState('landing');
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState(null);
  const [pendingTicker, setPendingTicker] = useState(null);

  // Check for existing session + handle verify token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get('verify');
    const adminParam  = params.get('admin');

    // Admin route — ?admin=true
    if (adminParam === 'true') {
      window.history.replaceState({}, '', '/');
      setView('admin');
      setAuthChecked(true);
      return;
    }

    // If verify token in URL, handle it first
    if (verifyToken) {
      if (window.__verifyHandled) {
        setView('auth-login');
        setAuthChecked(true);
        return;
      }
      window.__verifyHandled = true;

      api.get(`/api/auth/verify?token=${verifyToken}`)
        .then(res => {
          setVerifyMessage({ type: 'success', text: res.data.message || 'Email verified! You can now log in.' });
          setView('auth-login');
          setAuthChecked(true);
          window.history.replaceState({}, '', '/');
        })
        .catch(err => {
          setVerifyMessage({ type: 'error', text: err.response?.data?.detail || 'Verification failed. Link may have expired.' });
          setView('auth-login');
          setAuthChecked(true);
          window.history.replaceState({}, '', '/');
        });
      return;
    }

    // Normal session check
    const token = localStorage.getItem('gl_token');
    const savedUser = localStorage.getItem('gl_user');

    if (token && savedUser) {
      api.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          setUser(res.data);
          setView('dashboard');
          setAuthChecked(true);
        })
        .catch(() => {
          localStorage.removeItem('gl_token');
          localStorage.removeItem('gl_user');
          setAuthChecked(true);
        });
    } else {
      setAuthChecked(true);
    }
  }, []);

  // Listen for custom navigation events
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === 'legal') setView('legal');
    };
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, []);

  const handleAuth = (userData, token) => {
    setUser(userData);
    setView('landing');
    // Track login in PostHog
    if (window.posthog) {
      window.posthog.identify(userData.id || userData.email, {
        email: userData.email,
        display_name: userData.display_name,
      });
      window.posthog.capture('user_logged_in', {
        email: userData.email,
        display_name: userData.display_name,
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('gl_token');
    localStorage.removeItem('gl_user');
    setUser(null);
    setView('landing');
  };


  if (!authChecked) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0d1117',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ color: '#64748b', fontSize: '14px' }}>Loading...</div>
      </div>
    );
  }

  if (view === 'admin') {
    return <AdminDashboard onBack={() => setView('landing')} />;
  }

  if (view === 'legal') {
    return <LegalPage onBack={() => setView('landing')} />;
  }

  if (view === 'learn') {
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 0);
    return (
      <div style={{ minHeight: '100vh', background: '#0d1117', color: '#e4e4e7', fontFamily: "-apple-system, 'SF Pro Display', system-ui, sans-serif" }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(10,10,12,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 24px' }}>
          <div style={{ maxWidth: 800, margin: '0 auto', height: 52, display: 'flex', alignItems: 'center', gap: 14 }}>
            <button onClick={() => setView('landing')} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '6px 14px', color: '#a1a1aa', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
              Home
            </button>
          </div>
        </div>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
          <LearnTab />
        </div>
      </div>
    );
  }

  if (view === 'dashboard') {
    if (!user) {
      setView('auth');
      return null;
    }
    return (
      <Dashboard
        onBack={() => setView('landing')}
        user={user}
        onLogout={handleLogout}
        onLogin={() => setView('auth-login')}
        onSignup={() => setView('auth')}
        initialTicker={pendingTicker}
      />
    );
  }

  if (view === 'auth' || view === 'auth-login') {
    return (
      <AuthPage
        mode={view === 'auth-login' ? 'login' : 'signup'}
        onAuth={handleAuth}
        verifyMessage={verifyMessage}
      />
    );
  }

  return (
    <HomeFeed
      loggedIn={!!user}
      user={user}
      onSignIn={() => setView('auth-login')}
      onSignUp={() => setView('auth')}
      onLogout={handleLogout}
      onDashboard={(ticker) => {
        setPendingTicker(ticker || null);
        if (user) {
          setView('dashboard');
        } else {
          setView('auth-login');
        }
      }}
      onLearn={() => setView('learn')}
    />
  );
}

export default App;
